import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function ForecastPanel({ forecast }) {
  const { t } = useApp();
  if (!forecast || !forecast.ok) return null;
  const { p_up, p_down, quantiles, expected_move_pct, horizon_bars, confidence_interval_95_pct } = forecast;
  const skew = p_up >= 55 ? "bullish" : p_up <= 45 ? "bearish" : "neutral";
  const skewColor = skew === "bullish" ? "text-up" : skew === "bearish" ? "text-down" : "text-warn";
  const Icon = skew === "bullish" ? TrendingUp : skew === "bearish" ? TrendingDown : null;

  return (
    <div className="panel p-4 space-y-3" data-testid="forecast-panel">
      <div className="flex items-center justify-between">
        <div className="panel-title text-[14px]">{t("forecast.title", "Prévision probabiliste")}</div>
        <span className="text-[10px] uppercase tracking-widest text-white/40">Monte-Carlo · {horizon_bars}</span>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-up flex items-center gap-1"><TrendingUp size={12} /> {t("forecast.p_up", "P(hausse)")}</span>
          <span className="text-down flex items-center gap-1"><TrendingDown size={12} /> {t("forecast.p_down", "P(baisse)")}</span>
        </div>
        <div className="flex h-2.5 rounded-full overflow-hidden">
          <div style={{ width: `${p_up}%`, background: "var(--up)" }} />
          <div style={{ width: `${p_down}%`, background: "var(--down)" }} />
        </div>
        <div className="flex items-center justify-between text-xs font-mono">
          <span className="text-up">{p_up}%</span>
          <span className="text-down">{p_down}%</span>
        </div>
      </div>

      <div className="flex items-center justify-between px-3 py-2 rounded-md bg-hover border border-white/5">
        <span className="text-xs text-white/60">{t("forecast.expected", "Mouvement attendu")}</span>
        <span className={`font-mono text-sm ${skewColor}`}>
          {Icon && <Icon size={12} className="inline mr-1" />}
          {expected_move_pct >= 0 ? "+" : ""}{expected_move_pct}%
        </span>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{t("forecast.range", "Fourchette de prix probable")}</div>
        <div className="grid grid-cols-5 gap-1">
          <Q label="q05" value={quantiles.q05} extreme />
          <Q label="q25" value={quantiles.q25} />
          <Q label="q50" value={quantiles.q50} highlight />
          <Q label="q75" value={quantiles.q75} />
          <Q label="q95" value={quantiles.q95} extreme />
        </div>
        <div className="text-[10px] text-white/40 mt-2 font-mono">
          IC 95% : [{confidence_interval_95_pct.low} — {confidence_interval_95_pct.high}]
        </div>
      </div>
    </div>
  );
}

function Q({ label, value, extreme, highlight }) {
  return (
    <div className={`px-1 py-1.5 rounded text-center border ${
      highlight ? "border-accent-blue/60 bg-accent-blue/10" :
      extreme ? "border-white/5 bg-white/[0.02]" :
      "border-white/5"
    }`}>
      <div className="text-[9px] text-white/40">{label}</div>
      <div className="font-mono text-[11px]">{value}</div>
    </div>
  );
}
